import _reject from 'lodash-es/reject';
import _orderBy from 'lodash-es/orderBy';
import _intersection from 'lodash-es/intersection';
import { writable } from 'svelte/store';

import { alphabetStore, fullAlphabet } from '../store/alphabet';
import { itemStore } from '../store/items';
import { busyState } from '../store/busy';
import { lettersWithItemsStore } from '../store/letters-with-items';

const TAG_SCORE_MULTIPLIER = 3;
const TITLE_SCORE_MULTIPLIER = 2;
const CONTENT_SCORE_MULTIPLIER = 1;

export const SEARCH_TYPES = {
    ALPHA: 'alpha',
    FULLTEXT: 'ft',
};

const LAST_SEARCH_DEFAULT_STATE = {
    type: null,
    search: null,
};

export const lastSearchStore = writable(LAST_SEARCH_DEFAULT_STATE);

export class Search {
    constructor() {
        this.items = [];
        this.alphaIndex = {};
        this.lastSearch = LAST_SEARCH_DEFAULT_STATE;

        this.onAlphabetUpdate = this.onAlphabetUpdate.bind(this);
        this.indexItems = this.indexItems.bind(this);

        itemStore.subscribeItems(async (items) => {
            this.items = items;
            await busyState.exec(this.indexItems, items);
            switch (this.lastSearch.type) {
                case SEARCH_TYPES.ALPHA:
                    this.searchByFirstLetter(this.lastSearch.search);
                    break;
                case SEARCH_TYPES.FULLTEXT:
                    this.fulltextSearch(this.lastSearch.search);
                    break;
            }
        });
        alphabetStore.subscribe(this.onAlphabetUpdate);
    }

    indexItems(items) {
        this.alphaIndex = {};
        items.forEach((item) => {
            let firstLetter = item.title[0].toLowerCase();

            // Handle umlauts
            switch(firstLetter) {
                case 'ä':
                case 'Ä':
                    firstLetter = 'a';
                    break;
                case 'ö':
                case 'Ö':
                    firstLetter = 'o';
                    break;
                case 'ü':
                case 'Ü':
                    firstLetter = 'u';
                    break;
            }

            this.alphaIndex[firstLetter] = this.alphaIndex[firstLetter] || [];
            this.alphaIndex[firstLetter].push(item);
        });
        lettersWithItemsStore.update(() => this.getLettersWithItems());
    }

    onAlphabetUpdate(state) {
        if (state.active) {
            this.lastSearch = {
                type: SEARCH_TYPES.ALPHA,
                search: state.active,
            };
            lastSearchStore.update(() => this.lastSearch);
            this.searchByFirstLetter(state.active);
        }
    }

    updateFulltextSearch(terms) {
        if (this.lastSearch.search !== terms) {
            this.lastSearch = {
                type: SEARCH_TYPES.FULLTEXT,
                search: terms,
            };
            lastSearchStore.update(() => this.lastSearch);
            alphabetStore.unsetAlphabetSearch();
            this.fulltextSearch(terms);
        }
    }

    searchByFirstLetter(letter) {
        if (this.alphaIndex[letter]) {
            itemStore.updateVisibleItems(this.alphaIndex[letter]);
        }
    }

    fulltextSearch(string) {
        const terms = string.split(' ');

        let items = [...this.items];

        terms.forEach((term) => {
            items = items.map((item) => {
                return {
                    ...item,
                    score:
                        (item?.score || 0) +
                        this.tagScore(term, item) +
                        this.titleScore(term, item) +
                        this.contentScore(term, item),
                };
            });
        });

        const hits = _reject(items, ({ score }) => score < 1);
        const sorted = _orderBy(hits, ['score'], 'desc');

        itemStore.updateVisibleItems(sorted.length ? sorted : false);
    }

    tagScore(term, item) {
        if (item.tags.indexOf(term) > -1) {
            return TAG_SCORE_MULTIPLIER;
        }
        return 0;
    }

    titleScore(term, item) {
        const re = new RegExp(term, 'i');
        if (re.test(item.title)) {
            return TITLE_SCORE_MULTIPLIER;
        }
        return 0;
    }

    contentScore(term, item) {
        const re = new RegExp(term, 'i');
        if (re.test(item.content)) {
            const instances = re.exec(item.content).length;
            return instances * CONTENT_SCORE_MULTIPLIER;
        }
        return 0;
    }

    getLettersWithItems() {
        const existingLetters = Object.keys(this.alphaIndex);
        const sorted = _intersection(fullAlphabet, existingLetters);

        return sorted;
    }
}

export const search = new Search();
